import React, { useState, useEffect, useRef } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { collection, addDoc, query, orderBy, onSnapshot, serverTimestamp } from 'firebase/firestore';
import { db } from '../../../firebase';

const WHATSAPP_NUMBER = '919616622620'; // Replace with your WhatsApp number
const ChatBot = () => {
  const [isOpen, setIsOpen] = useState(true); // Open by default for greeting
  const [message, setMessage] = useState('');
  const [messages, setMessages] = useState([
    {
      id: 'greeting',
      text: "👋 Welcome to BuildMyWealth! I'm your investment assistant. How can I help you today?",
      sender: 'bot',
      timestamp: new Date(),
    },
  ]);
  const [step, setStep] = useState('greeting');
  const [lead, setLead] = useState({ phone: '', email: '', wantsWhatsapp: false });
  const [botTyping, setBotTyping] = useState(false);
  const messagesEndRef = useRef(null);
  const chatBoxRef = useRef(null);

  // Use a shared chat collection for all users (anonymous)
  useEffect(() => {
    const q = query(
      collection(db, `chats/public/messages`),
      orderBy('timestamp', 'asc')
    );
    const unsubscribe = onSnapshot(q, (querySnapshot) => {
      const messageList = [];
      querySnapshot.forEach((doc) => {
        messageList.push({ id: doc.id, ...doc.data() });
      });
      setMessages(messageList);
    });
    return () => unsubscribe();
  }, []);

  useEffect(() => {
    scrollToBottom();
  }, [messages]);

  const scrollToBottom = () => {
    messagesEndRef.current?.scrollIntoView({ behavior: 'smooth' });
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!message.trim()) return;
    const userMsg = {
      id: Date.now(),
      text: message,
      sender: 'user',
      timestamp: new Date(),
    };
    setMessages((prev) => [...prev, userMsg]);
    processBotStep(message);
    setMessage('');
  };

  // Add a helper to simulate typing delay
  const botReply = (msgObj, delay = 700) => {
    setBotTyping(true);
    setTimeout(() => {
      setMessages((prev) => [...prev, msgObj]);
      setBotTyping(false);
    }, delay);
  };

  // Simple state machine for lead gen
  const processBotStep = (userInput) => {
    if (step === 'greeting') {
      botReply({
        id: 'ask-goal',
        text: `Great! What brings you here today? Please reply with a number:

*1.* Build Wealth
*2.* Save for a Goal
*3.* General Query
*4.* Other`,
        sender: 'bot',
        timestamp: new Date(),
      });
      setStep('ask-goal');
      return;
    }
    if (step === 'ask-goal') {
      let option = userInput.trim();
      if (/^(1|build|wealth|invest|grow|money)/i.test(option)) {
        botReply({
          id: 'ask-phone',
          text: "Awesome! To provide personalized advice, may I have your phone number?",
          sender: 'bot',
          timestamp: new Date(),
        });
        setStep('ask-phone');
      } else if (/^(2|goal|save)/i.test(option)) {
        botReply({
          id: 'goal-detail',
          text: "What is your goal? (e.g., Child Education, Retirement, House Purchase, etc.)",
          sender: 'bot',
          timestamp: new Date(),
        });
        setStep('other-query');
      } else if (/^(3|query|question)/i.test(option)) {
        botReply({
          id: 'other-query',
          text: "Please share your query and I'll do my best to help!",
          sender: 'bot',
          timestamp: new Date(),
        });
        setStep('other-query');
      } else if (/^(4|other)/i.test(option)) {
        botReply({
          id: 'other-query',
          text: "Please share your query and I'll do my best to help!",
          sender: 'bot',
          timestamp: new Date(),
        });
        setStep('other-query');
      } else {
        botReply({
          id: 'invalid-option',
          text: "Please reply with 1, 2, 3, or 4 to choose an option.",
          sender: 'bot',
          timestamp: new Date(),
        });
      }
      return;
    }
    if (step === 'ask-phone') {
      // Accept international numbers, allow skip
      if (/^(skip)$/i.test(userInput)) {
        setLead((prev) => ({ ...prev, phone: '' }));
        botReply({
          id: 'ask-email',
          text: "No problem! Could you provide your email address? (or type 'skip')",
          sender: 'bot',
          timestamp: new Date(),
        });
        setStep('ask-email');
      } else if (/^(\+?\d{10,15})$/.test(userInput.replace(/\s+/g, ''))) {
        setLead((prev) => ({ ...prev, phone: userInput }));
        botReply({
          id: 'ask-email',
          text: "Thank you! Could you also provide your email address? (or type 'skip')",
          sender: 'bot',
          timestamp: new Date(),
        });
        setStep('ask-email');
      } else {
        botReply({
          id: 'invalid-phone',
          text: "Please enter a valid phone number (10-15 digits, with or without country code), or type 'skip'.",
          sender: 'bot',
          timestamp: new Date(),
        });
      }
      return;
    }
    if (step === 'ask-email') {
      if (/^(skip)$/i.test(userInput)) {
        setLead((prev) => ({ ...prev, email: '' }));
        botReply({
          id: 'ask-whatsapp',
          text: "Would you like to continue this chat on WhatsApp? (yes/no)",
          sender: 'bot',
          timestamp: new Date(),
        });
        setStep('ask-whatsapp');
      } else if (/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(userInput)) {
        setLead((prev) => ({ ...prev, email: userInput }));
        botReply({
          id: 'ask-whatsapp',
          text: "Would you like to continue this chat on WhatsApp? (yes/no)",
          sender: 'bot',
          timestamp: new Date(),
        });
        setStep('ask-whatsapp');
      } else {
        botReply({
          id: 'invalid-email',
          text: "Please enter a valid email address, or type 'skip'.",
          sender: 'bot',
          timestamp: new Date(),
        });
      }
      return;
    }
    if (step === 'ask-whatsapp') {
      if (/yes|y/i.test(userInput)) {
        setLead((prev) => ({ ...prev, wantsWhatsapp: true }));
        // Gather all conversation so far, format for WhatsApp
        const conversationText = messages
          .map((msg) => `${msg.sender === 'user' ? '*You*' : '*Bot*'}: ${msg.text.replace(/\n/g, '\n')}`)
          .join('\n');
        const whatsappMsg =
          `Hi, I was chatting on BuildMyWealth.net. Here is our conversation so far:\n\n${conversationText}`;
        // Store lead info and conversation in Firestore
        if (lead.phone) {
          const leadData = {
            phone: lead.phone,
            email: lead.email,
            wantsWhatsapp: true,
            conversation: messages.map((msg) => ({ sender: msg.sender, text: msg.text, timestamp: msg.timestamp })),
            createdAt: new Date().toISOString(),
          };
          import('../../../firebase').then(({ db }) => {
            import('firebase/firestore').then(({ doc, setDoc }) => {
              setDoc(doc(db, 'leadChats', lead.phone), leadData, { merge: true });
            });
          });
        }
        botReply({
          id: 'whatsapp-link',
          text: "Great! Click here to continue on WhatsApp: ",
          sender: 'bot',
          timestamp: new Date(),
          link: `https://wa.me/${WHATSAPP_NUMBER}?text=${encodeURIComponent(whatsappMsg)}`,
        });
        setStep('done');
      } else {
        // Store all completed chats in Firestore, even if WhatsApp is not chosen
        const storeChatInFirestore = (customLead = {}) => {
          const docKey = lead.phone ? lead.phone : `anon_${Date.now()}`;
          const leadData = {
            phone: lead.phone || '',
            email: lead.email || '',
            wantsWhatsapp: lead.wantsWhatsapp || false,
            ...customLead,
            conversation: messages.map((msg) => ({ sender: msg.sender, text: msg.text, timestamp: msg.timestamp })),
            createdAt: new Date().toISOString(),
          };
          import('../../../firebase').then(({ db }) => {
            import('firebase/firestore').then(({ doc, setDoc }) => {
              setDoc(doc(db, 'leadChats', docKey), leadData, { merge: true });
            });
          });
        };
        storeChatInFirestore({ wantsWhatsapp: false });
        botReply({
          id: 'thank-you',
          text: "Thank you! Our advisor will contact you soon.",
          sender: 'bot',
          timestamp: new Date(),
        });
        setStep('done');
      }
      return;
    }
    // fallback for other queries
    if (step === 'other-query') {
      botReply({
        id: 'ask-whatsapp-other',
        text: "Would you like to continue this chat on WhatsApp? (yes/no)",
        sender: 'bot',
        timestamp: new Date(),
      });
      setStep('ask-whatsapp-other');
      return;
    }
    // Handle WhatsApp offer for non-lead paths
    if (step === 'ask-whatsapp-other') {
      if (/yes|y/i.test(userInput)) {
        setLead((prev) => ({ ...prev, wantsWhatsapp: true }));
        // Gather all conversation so far, format for WhatsApp
        const conversationText = messages
          .map((msg) => `${msg.sender === 'user' ? '*You*' : '*Bot*'}: ${msg.text.replace(/\n/g, '\n')}`)
          .join('\n');
        const whatsappMsg =
          `Hi, I was chatting on BuildMyWealth.net. Here is our conversation so far:\n\n${conversationText}`;
        // Store lead info and conversation in Firestore (no phone, so use timestamp as key)
        const docKey = lead.phone ? lead.phone : `anon_${Date.now()}`;
        const leadData = {
          phone: lead.phone || '',
          email: lead.email || '',
          wantsWhatsapp: true,
          conversation: messages.map((msg) => ({ sender: msg.sender, text: msg.text, timestamp: msg.timestamp })),
          createdAt: new Date().toISOString(),
        };
        import('../../../firebase').then(({ db }) => {
          import('firebase/firestore').then(({ doc, setDoc }) => {
            setDoc(doc(db, 'leadChats', docKey), leadData, { merge: true });
          });
        });
        botReply({
          id: 'whatsapp-link-other',
          text: "Great! Click here to continue on WhatsApp: ",
          sender: 'bot',
          timestamp: new Date(),
          link: `https://wa.me/${lead.phone || ''}?text=${encodeURIComponent(whatsappMsg)}`,
        });
        setStep('done');
      } else {
        // Store all completed chats in Firestore, even if WhatsApp is not chosen
        const storeChatInFirestore = (customLead = {}) => {
          const docKey = lead.phone ? lead.phone : `anon_${Date.now()}`;
          const leadData = {
            phone: lead.phone || '',
            email: lead.email || '',
            wantsWhatsapp: lead.wantsWhatsapp || false,
            ...customLead,
            conversation: messages.map((msg) => ({ sender: msg.sender, text: msg.text, timestamp: msg.timestamp })),
            createdAt: new Date().toISOString(),
          };
          import('../../../firebase').then(({ db }) => {
            import('firebase/firestore').then(({ doc, setDoc }) => {
              setDoc(doc(db, 'leadChats', docKey), leadData, { merge: true });
            });
          });
        };
        storeChatInFirestore({ wantsWhatsapp: false });
        botReply({
          id: 'thank-you-other',
          text: "Thank you! Our advisor will contact you soon.",
          sender: 'bot',
          timestamp: new Date(),
        });
        setStep('done');
      }
      return;
    }
  };

  return (
    <>
      {/* Chat Icon */}
      <motion.div
        className="fixed bottom-6 right-6 z-50"
        whileHover={{ scale: 1.1 }}
        whileTap={{ scale: 0.9 }}
      >
        <button
          onClick={() => setIsOpen(!isOpen)}
          className="w-14 h-14 bg-gradient-to-r from-indigo-600 to-purple-600 rounded-full shadow-lg flex items-center justify-center text-white hover:shadow-xl transition-all duration-300"
        >
          {isOpen ? (
            <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M6 18L18 6M6 6l12 12" />
            </svg>
          ) : (
            <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M8 10h.01M12 10h.01M16 10h.01M9 16H5a2 2 0 01-2-2V6a2 2 0 012-2h14a2 2 0 012 2v8a2 2 0 01-2 2h-5l-5 5v-5z" />
            </svg>
          )}
        </button>
      </motion.div>

      {/* Chat Window */}
      <AnimatePresence>
        {isOpen && (
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            exit={{ opacity: 0, y: 20 }}
            className="fixed bottom-24 right-1 left-1 sm:right-6 sm:left-auto w-auto max-w-full sm:max-w-md md:w-96 md:max-w-md bg-white rounded-2xl shadow-2xl z-50 overflow-hidden"
          >
            {/* Chat Header */}
            <div className="bg-gradient-to-r from-indigo-600 to-purple-600 p-4 flex items-center justify-between">
              <div>
                <h3 className="text-white font-medium">Investment Assistant</h3>
                <p className="text-purple-200 text-sm">
                  Chat with our investment assistant below.
                </p>
              </div>
              <button
                onClick={() => {
                  setMessages([
                    {
                      id: 'greeting',
                      text: "👋 Welcome to BuildMyWealth! I'm your investment assistant. How can I help you today?",
                      sender: 'bot',
                      timestamp: new Date(),
                    },
                  ]);
                  setStep('greeting');
                  setLead({ phone: '', email: '', wantsWhatsapp: false });
                }}
                className="ml-2 px-3 py-1 bg-white text-indigo-600 rounded-full text-xs font-semibold shadow hover:bg-gray-100 transition"
              >
                Restart
              </button>
            </div>

            {/* Chat Messages */}
            <div 
              ref={chatBoxRef}
              className="h-96 max-h-[60vh] overflow-y-auto p-4 space-y-4"
            >
              {messages.map((msg) => (
                <div
                  key={msg.id}
                  className={`flex ${msg.sender === 'user' ? 'justify-end' : 'justify-start'}`}
                >
                  <div
                    className={`max-w-[80%] rounded-2xl px-4 py-2 ${
                      msg.sender === 'user'
                        ? 'bg-indigo-600 text-white rounded-br-none'
                        : 'bg-gray-100 text-gray-800 rounded-bl-none'
                    }`}
                  >
                    {msg.text}
                    {msg.link && (
                      <a
                        href={msg.link}
                        target="_blank"
                        rel="noopener noreferrer"
                        className="block text-green-600 underline mt-1"
                      >
                        Continue on WhatsApp
                      </a>
                    )}
                  </div>
                </div>
              ))}
              {botTyping && (
                <div className="flex justify-start">
                  <div className="max-w-[80%] rounded-2xl px-4 py-2 bg-gray-100 text-gray-800 rounded-bl-none animate-pulse">
                    <span className="inline-block w-2 h-2 bg-gray-400 rounded-full mr-1"></span>
                    <span className="inline-block w-2 h-2 bg-gray-400 rounded-full mr-1"></span>
                    <span className="inline-block w-2 h-2 bg-gray-400 rounded-full"></span>
                  </div>
                </div>
              )}
              <div ref={messagesEndRef} />
            </div>

            {/* Chat Input */}
            <form onSubmit={handleSubmit} className="border-t p-4">
              <div className="flex space-x-2">
                <input
                  type="text"
                  value={message}
                  onChange={(e) => setMessage(e.target.value)}
                  placeholder="Type your message..."
                  className="flex-1 px-4 py-2 border rounded-full focus:outline-none focus:ring-2 focus:ring-indigo-500 focus:border-transparent text-sm md:text-base"
                />
                <button
                  type="submit"
                  className="bg-indigo-600 text-white rounded-full p-2 hover:bg-indigo-700 transition-colors"
                >
                  <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M12 19l9 2-9-18-9 18 9-2zm0 0v-8" />
                  </svg>
                </button>
              </div>
            </form>
          </motion.div>
        )}
      </AnimatePresence>
    </>
  );
};

export default ChatBot;